class ProductCardSlider extends HTMLElement {
  constructor() {
    super();

    this.slides = Array.from(this.querySelectorAll('picture'));
    this.currentIndex = 0;
    this.sliderContainer = null;
    this.paginationContainer = null;
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.isDragging = false;
    this.isDesktop = window.matchMedia('(min-width: 1024px)').matches;

    this.isMobileDisabled = this.hasAttribute('data-mobile-disabled');

    if (this.slides.length > 1) {
        this.initSlider();
    }

    // Écouter les changements de taille d'écran
    this.mediaQuery = window.matchMedia('(min-width: 1024px)');
    this.mediaQuery.addEventListener('change', (e) => {
      this.isDesktop = e.matches;
      this.updateBehavior();
    });
  }

  initSlider() {
    this.classList.add('product-card-slider');
    
    this.sliderContainer = document.createElement('div');
    this.sliderContainer.classList.add('slider-container');
    
    this.slides.forEach((slideContent) => {
      const slide = document.createElement('div');
      slide.classList.add('slider-slide');
      
      const spinner = document.createElement('div');
      spinner.classList.add('slide-spinner');
      slide.appendChild(spinner);

      const img = slideContent.querySelector('img');
      if (img.complete) {
        spinner.style.display = 'none';
      } else {
        img.addEventListener('load', () => {
          spinner.style.display = 'none';
        }, { once: true });
        img.addEventListener('error', () => {
          spinner.style.display = 'none';
        }, { once: true });
      }

      slide.appendChild(slideContent);
      this.sliderContainer.appendChild(slide);
    });

    this.appendChild(this.sliderContainer);
    this.createPagination();
    
    // Initialiser le comportement selon le device
    this.updateBehavior();

    // Masquer la pagination si mobile désactivé
    if (!this.isDesktop && this.isMobileDisabled && this.paginationContainer) {
      this.paginationContainer.style.display = 'none';
    }
  }

  updateBehavior() {
    if (this.isDesktop) {
      // Desktop: hover behavior
      this.classList.add('desktop-mode');
      this.removeEventListener('mousedown', this.handleMouseDown);
      this.addEventListener('mouseenter', () => this.handleDesktopHover());
      this.addEventListener('mouseleave', () => this.handleDesktopLeave());
    } else {
      // Mobile: swipe behavior
      this.classList.remove('desktop-mode');
      this.removeEventListener('mouseenter', this.handleDesktopHover);
      this.removeEventListener('mouseleave', this.handleDesktopLeave);
      
      if (!this.isMobileDisabled) {
        // Touch events (already attached in initSlider for mobile)
        this.addEventListener('touchstart', e => this.handleTouchStart(e), { passive: true });
        this.addEventListener('touchmove', e => this.handleTouchMove(e), { passive: true });
        this.addEventListener('touchend', () => this.handleTouchEnd());
        
        // Desktop drag (only for tablet/small screens)
        this.addEventListener('mousedown', e => this.handleMouseDown(e));
      } else {
        // Remove listeners if they were previously added (e.g. on resize)
        this.removeEventListener('touchstart', this.handleTouchStart);
        this.removeEventListener('touchmove', this.handleTouchMove);
        this.removeEventListener('touchend', this.handleTouchEnd);
        this.removeEventListener('mousedown', this.handleMouseDown);

        // Reset position if disabled on mobile
        this.currentIndex = 0;
        if (this.sliderContainer) {
          this.sliderContainer.style.transform = 'translateX(0)';
          this.sliderContainer.style.transition = 'none';
        }
      }
    }

    if (this.paginationContainer) {
      this.paginationContainer.style.display = (this.isDesktop || !this.isMobileDisabled) ? '' : 'none';
      if (!this.isDesktop && this.isMobileDisabled) {
        this.paginationContainer.style.display = 'none';
      }
    }
  }

  handleDesktopHover() {
    // En desktop, au hover, afficher la deuxième image instantanément
    this.showSlide(1, true); // true = instant, sans transition
  }

  handleDesktopLeave() {
    // Quand on quitte le hover, revenir à la première image
    this.showSlide(0, true); // true = instant, sans transition
  }

  createPagination() {
    this.paginationContainer = document.createElement('div');
    this.paginationContainer.classList.add('slider-pagination');
    
    this.slides.forEach((_, index) => {
      const dot = document.createElement('span');
      dot.classList.add('slider-pagination__dot');
      if (index === 0) {
        dot.classList.add('active');
      }
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.showSlide(index);
      });
      this.paginationContainer.appendChild(dot);
    });

    this.appendChild(this.paginationContainer);
  }

  handleTouchStart(e) {
    // Ne pas permettre le drag en desktop
    if (this.isDesktop) return;
    
    this.touchStartX = e.touches[0].clientX;
    this.touchEndX = e.touches[0].clientX;
    this.isDragging = true;
    this.sliderContainer.style.transition = 'none';
  }

  handleTouchMove(e) {
    if (!this.isDragging || this.isDesktop) return;
    this.touchEndX = e.touches[0].clientX;
    
    // Calculate drag distance
    const diff = this.touchEndX - this.touchStartX;
    const currentOffset = -this.currentIndex * 100;
    const dragPercentage = (diff / this.offsetWidth) * 100;
    
    // Apply transform in real-time
    this.sliderContainer.style.transform = `translateX(${currentOffset + dragPercentage}%)`;
  }

  handleTouchEnd() {
    if (!this.isDragging || this.isDesktop) return;
    this.isDragging = false;
    
    const swipeDistance = this.touchStartX - this.touchEndX;
    const swipeThreshold = 50;

    if (swipeDistance > swipeThreshold) {
      this.showSlide(this.currentIndex + 1);
    } else if (swipeDistance < -swipeThreshold) {
      this.showSlide(this.currentIndex - 1);
    } else {
      // Snap back to current slide
      this.showSlide(this.currentIndex);
    }

    // Reset
    this.touchStartX = 0;
    this.touchEndX = 0;
  }

  handleMouseDown(e) {
    // Ne pas permettre le drag en desktop
    if (this.isDesktop) return;
    
    this.isDragging = true;
    this.touchStartX = e.clientX;
    this.touchEndX = e.clientX;
    this.sliderContainer.style.transition = 'none';
    
    const handleMouseMove = (e) => {
      if (!this.isDragging) return;
      this.touchEndX = e.clientX;
      
      // Calculate drag distance
      const diff = this.touchEndX - this.touchStartX;
      const currentOffset = -this.currentIndex * 100;
      const dragPercentage = (diff / this.offsetWidth) * 100;
      
      // Apply transform in real-time
      this.sliderContainer.style.transform = `translateX(${currentOffset + dragPercentage}%)`;
    };
    
    const handleMouseUp = () => {
      if (!this.isDragging) return;
      this.isDragging = false;
      
      const swipeDistance = this.touchStartX - this.touchEndX;
      const swipeThreshold = 50;
      
      if (swipeDistance > swipeThreshold) {
        this.showSlide(this.currentIndex + 1);
      } else if (swipeDistance < -swipeThreshold) {
        this.showSlide(this.currentIndex - 1);
      } else {
        // Snap back to current slide
        this.showSlide(this.currentIndex);
      }
      
      this.touchStartX = 0;
      this.touchEndX = 0;
      
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }

  showSlide(index, instant = false) {
    const numSlides = this.slides.length;
    if (index >= numSlides) {
      this.currentIndex = 0;
    } else if (index < 0) {
      this.currentIndex = numSlides - 1;
    } else {
      this.currentIndex = index;
    }

    // En desktop avec instant = true, pas de transition
    // En mobile, toujours avec transition
    if (instant && this.isDesktop) {
      this.sliderContainer.style.transition = 'none';
    } else {
      this.sliderContainer.style.transition = 'transform 0.3s ease-out';
    }
    
    this.sliderContainer.style.transform = `translateX(-${this.currentIndex * 100}%)`;
    
    // Update pagination dots
    const dots = this.paginationContainer.querySelectorAll('.slider-pagination__dot');
    dots.forEach((dot, i) => {
      if (i === this.currentIndex) {
        dot.classList.add('active');
      } else {
        dot.classList.remove('active');
      }
    });
  }
}

customElements.get('product-card-slider') || customElements.define('product-card-slider', ProductCardSlider)

function setupCountryList() {
	const countryList = document.getElementById('CountryList');
	if (!countryList) return;

	// Initial backup of the original list items
	if (!countryList.dataset.initialHtml) {
		countryList.dataset.initialHtml = countryList.innerHTML;
	}

	const countryListItems = Array.from(
		new DOMParser().parseFromString(countryList.dataset.initialHtml, 'text/html').querySelectorAll('li')
	);

	const americasCode = ['US', 'CA', 'MX', 'BR', 'AR', 'CL', 'CO', 'PA'];
	const asiaCode = ['AU', 'JP', 'KR', 'CN', 'HK', 'MO', 'TW', 'IN', 'ID', 'PH', 'SG', 'TH', 'AE', 'BH', 'OM', 'QA', 'KW', 'SA', 'IL'];
	const europeCode = [
		'FR', 'DE', 'IT', 'ES', 'GB', 'NL', 'BE', 'AT', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'GR', 'HU', 'IS', 'IE',
		'LV', 'LI', 'LT', 'LU', 'MT', 'NO', 'PL', 'PT', 'RO', 'SK', 'SI', 'SE', 'CH', 'MC', 'AD', 'ME'
	];

	const americas = [];
	const asia = [];
	const europe = [];
	const other = [];

	countryListItems.forEach(country => {
		const continentOption = country.querySelector('[data-option]');
		if (!continentOption) { return }
		const continent = continentOption.dataset.option;
		if (!continent) { return }
		if (americasCode.includes(continent)) {
			americas.push(country);
		} else if (asiaCode.includes(continent)) {
			asia.push(country);
		} else if (europeCode.includes(continent)) {
			europe.push(country);
		} else {
			other.push(country);
		}
	});

	const sortByName = (a, b) => {
		const nameA = a.innerText.trim();
		const nameB = b.innerText.trim();
		return nameA.localeCompare(nameB, 'fr', { sensitivity: 'base' });
	};

	americas.sort(sortByName);
	asia.sort(sortByName);
	europe.sort(sortByName);
	other.sort(sortByName);

	function getNumberOfColumns() {
		const width = window.innerWidth;
		if (width >= 1600) {
			return 4;
		}
		if (width >= 1200) {
			return 3;
		}
		return 2;
	}

	function reorderForColumns(list, cols) {
		if (cols <= 1) {
			return list;
		}
		const reordered = [];
		const baseNumRows = Math.floor(list.length / cols);
		const extraItems = list.length % cols;
		const columns = [];

		let startIndex = 0;
		for (let c = 0; c < cols; c++) {
			const numRows = baseNumRows + (c < extraItems ? 1 : 0);
			columns.push(list.slice(startIndex, startIndex + numRows));
			startIndex += numRows;
		}

		const maxRows = columns[0] ? columns[0].length : 0;
		for (let r = 0; r < maxRows; r++) {
			for (let c = 0; c < cols; c++) {
				if (columns[c] && columns[c][r]) {
					reordered.push(columns[c][r]);
				}
			}
		}

		return reordered;
	}

	// Réorganiser asia pour placer HK, MO, TW juste après CN
	const chinaCodes = ['CN', 'HK', 'MO', 'TW'];
	const chinaRegions = [];
	let cnIndex = -1;
	
	// Trouver CN et extraire HK, MO, TW
	asia.forEach((li, index) => {
		const el = li.querySelector('[data-option]');
		const code = el && el.dataset.option;
		if (code === 'CN') {
			cnIndex = index;
		} else if (code && chinaCodes.includes(code)) {
			chinaRegions.push({ item: li, code: code });
		}
	});
	
	// Retirer HK, MO, TW de la liste
	chinaRegions.forEach(region => {
		const idx = asia.indexOf(region.item);
		if (idx > -1) {
			asia.splice(idx, 1);
			if (idx < cnIndex) {
				cnIndex--;
			}
		}
	});
	
	// Trier les régions chinoises : HK, MO, TW
	const regionOrder = { 'HK': 0, 'MO': 1, 'TW': 2 };
	chinaRegions.sort((a, b) => regionOrder[a.code] - regionOrder[b.code]);
	
	// Insérer HK, MO, TW juste après CN
	if (cnIndex > -1 && chinaRegions.length > 0) {
		chinaRegions.forEach((region, i) => {
			asia.splice(cnIndex + 1 + i, 0, region.item);
		});
	}

	const numCols = getNumberOfColumns();

	const reorderedAmericas = reorderForColumns(americas, numCols);
	const reorderedAsia = reorderForColumns(asia, numCols);
	const reorderedEurope = reorderForColumns(europe, numCols);
	const reorderedOther = reorderForColumns(other, numCols);

	countryList.innerHTML = '';

	if (americas.length > 0) {
		let blockHTML = `<li class="continent-group americas-group">`;
		blockHTML += `<div class="continent--title mb-[8px] w-full px-[12px] lg:px-[16px]">${window.localizationStrings.america}</div>`;
		blockHTML += `<ul class="countries-sublist">`;
		reorderedAmericas.forEach(countryName => {
			blockHTML += countryName.outerHTML;
		});
		blockHTML += `</ul>`;
		blockHTML += `</li>`;
		countryList.innerHTML += blockHTML;
	}

	if (asia.length > 0) {
		let blockHTML = `<li class=\"continent-group asia-group\">`;
		blockHTML += `<div class=\"continent--title mb-[8px] w-full px-[12px] lg:px-[16px]\">${window.localizationStrings.asia}</div>`;
		blockHTML += `<ul class=\"countries-sublist\">`;

		// Détection de la langue actuelle
		const currentLang = document.documentElement.lang || 'en';
		const isFrench = currentLang.startsWith('fr');

		// Render tous les pays d'Asie
		reorderedAsia.forEach(countryItem => {
			const html = countryItem.outerHTML;
			const match = html.match(/data-option=\"([A-Z]{2})\"/);
			const code = match && match[1];
			
			// CN : FR => Chine (continentale) & EN => China (Mainland)
			if (code === 'CN') {
				let modifiedHtml = html;
				const textMatch = modifiedHtml.match(/<span class="text-left inline[^>]*>([^<]+)<\/span>/);
				const countryText = textMatch ? textMatch[1] : '';
				let finalCountryText = countryText;

				if (isFrench) {
					finalCountryText = 'Chine (continentale)';
				} else {
					finalCountryText = 'China (Mainland)';
				}

				modifiedHtml = modifiedHtml.replace(/<span class="text-left inline[^>]*>([^<]+)<\/span>/, `<span class="btn btn-link !p-0 min-h-fit leading-[16px] inline">${finalCountryText}</span>`);

				blockHTML += modifiedHtml;
			}
			// HK, MO, TW : appliquer les transformations
      else if (code && ['CN', 'HK', 'MO', 'TW'].includes(code)) {
        let modifiedHtml = html;

        // Extraire le texte du pays
        const textMatch = modifiedHtml.match(/<span class="text-left inline[^>]*>([^<]+)<\/span>/);
        const countryText = textMatch ? textMatch[1] : '';
        let finalCountryText = countryText;

        // En français pour HK et MO : Remplacer SAR par RAS et retirer 'chinoise'
        if (isFrench && (code === 'HK' || code === 'MO')) {
          finalCountryText = finalCountryText.replace(/\bR\.A\.S\./g, 'RAS').replace(/\bchinoise\b/g, '');
        }

        // Pour Taiwan : Région de Taïwan (en français) ou Region of Taiwan (en anglais)
        if (code === 'TW') {
          if (isFrench) {
            finalCountryText = finalCountryText.replace(/(Ta[ïi]wan)/g, 'Région de $1');
          } else {
            finalCountryText = finalCountryText.replace(/(Taiwan)/g, '$1 Region');
          }
        }

        // Ajouter le marker noir avant le texte
				const blackDot = '<span>&nbsp;•&nbsp;&nbsp;</span>';
				const wrappedText = `<span class="btn btn-link !p-0 min-h-fit leading-[16px] inline">${finalCountryText}</span>`;
				modifiedHtml = modifiedHtml.replace(/<span class="text-left inline btn btn-link[^>]*>[^<]+<\/span>/, blackDot + wrappedText);
				
				blockHTML += modifiedHtml;
      }
      // Autres pays : affichage normal
      else {
        blockHTML += html;
      }
		});

		blockHTML += `</ul>`;
		blockHTML += `</li>`;
		countryList.innerHTML += blockHTML;
	}

	if (europe.length > 0) {
		let blockHTML = `<li class="continent-group europe-group">`;
		blockHTML += `<div class="continent--title mb-[8px] w-full px-[12px] lg:px-[16px]">${window.localizationStrings.europe}</div>`;
		blockHTML += `<ul class="countries-sublist">`;
		reorderedEurope.forEach(countryName => {
			blockHTML += countryName.outerHTML;
		});
		blockHTML += `</ul>`;
		blockHTML += `</li>`;
		countryList.innerHTML += blockHTML;
	}

	if (other.length > 0) {
		let blockHTML = `<li class="continent-group other-group">`;
		blockHTML += `<div class="continent--title mb-[8px] w-full px-[12px] lg:px-[16px]">${window.localizationStrings.other}</div>`;
		blockHTML += `<ul class="countries-sublist">`;
		reorderedOther.forEach(countryName => {
			blockHTML += countryName.outerHTML;
		});
		blockHTML += `</ul>`;
		blockHTML += `</li>`;
		countryList.innerHTML += blockHTML;
	}
}

document.addEventListener('DOMContentLoaded', () => {
	setupCountryList();
	window.addEventListener('resize', setupCountryList);
});

class SizeGuideTabs extends HTMLElement {
	constructor() {
		super();
	}

	connectedCallback() {
		this.buttons = this.querySelectorAll('[data-size-guide-btn]');
		this.tabs = this.querySelectorAll('[data-size-guide-tab-item]');

		if (!this.buttons?.length > 0 || !this.tabs?.length > 0) return;

		this.buttons.forEach(button => {
			button.addEventListener('click', () => this.handleButtonClick(button));
		});
	}

	disconnectedCallback() {
		this.buttons?.forEach(button => {
			button.removeEventListener('click', () => this.handleButtonClick(button));
		});
	}

	handleButtonClick(button) {
		this.buttons.forEach(button => button.classList.remove('active'));
		button.classList.add('active');

		this.tabs.forEach(tab => {
			if (tab.dataset.sizeGuideTab === button.dataset.sizeGuideTab) {
				tab.classList.add('active');
			} else {
				tab.classList.remove('active');
			}
		});
	}
}

customElements.get('size-guide-tabs') || customElements.define('size-guide-tabs', SizeGuideTabs);